import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check, Gem, Mic, Sparkles, Volume2, Wand2, Zap } from "lucide-react";
import { toast } from "sonner";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { VoiceChangeCta } from "@/components/voice/VoiceChangeOption";
import { StepIndicator } from "@/components/create/StepIndicator";
import { useCredits } from "@/hooks/useCredits";

// 「もう一回作る」から渡される素材URLを受け取る
const qualitySearchSchema = z.object({
  image: fallback(z.string().optional(), undefined).optional(),
  video: fallback(z.string().optional(), undefined).optional(),
});

export const Route = createFileRoute("/_authenticated/create/quality")({
  head: () => ({
    meta: [
      { title: "品質を選択 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: zodValidator(qualitySearchSchema),
  component: QualityPage,
});

const WIZARD_KEY = "create:wizard";
const QUALITY_KEY = "create:quality";
const WATERMARK_REMOVE_COST = 10;

type TierId =
  | "trial"
  | "standard"
  | "high"
  | "premium"
  | "lipsync"
  | "i2v_standard"
  | "i2v_high"
  | "i2v_long"
  | "story_std"
  | "story_pro";

type CreateMode = "faceswap" | "i2v" | "story";

type Tier = {
  id: TierId;
  mode: CreateMode | "lipsync";
  name: string;
  description: string;
  tickets: number;
  jpy: number;
  estimatedTime: string;
  resolution: string;
  duration: string;
  hasAudio?: boolean;
  needsScript?: boolean;
  icon: typeof Sparkles;
};

const TIERS: Tier[] = [
  {
    id: "trial",
    mode: "faceswap",
    name: "お試し変身",
    description: "顔を入れ替えて10秒の動画に。アカウントごとに2回まで無料でお試し。",
    tickets: 20,
    jpy: 200,
    estimatedTime: "約5〜10分で完成",
    resolution: "720p",
    duration: "10秒",
    icon: Zap,
  },
  {
    id: "standard",
    mode: "faceswap",
    name: "標準動画生成",
    description: "10秒のしっかりした動画。SNS投稿にちょうど良い品質。",
    tickets: 12,
    jpy: 120,
    estimatedTime: "約3〜6分で完成",
    resolution: "720p",
    duration: "10秒",
    icon: Sparkles,
  },
  {
    id: "high",
    mode: "faceswap",
    name: "高品質動画",
    description: "10秒・高画質。表情やディテールがくっきり再現されます。",
    tickets: 40,
    jpy: 400,
    estimatedTime: "約10〜15分で完成",
    resolution: "1080p",
    duration: "10秒",
    icon: Wand2,
  },
  {
    id: "premium",
    mode: "faceswap",
    name: "本格動画（音声付き）",
    description: "10秒・最高画質・音声付き。ショート動画コンテンツに最適。",
    tickets: 50,
    jpy: 500,
    estimatedTime: "約10〜15分で完成",
    resolution: "1080p",
    duration: "10秒",
    hasAudio: true,
    icon: Volume2,
  },
  {
    id: "i2v_standard",
    mode: "i2v",
    name: "プロンプト生成（標準）",
    description: "1枚の画像とプロンプトから5秒のAI動画を生成。",
    tickets: 25,
    jpy: 250,
    estimatedTime: "約5〜10分で完成",
    resolution: "720p",
    duration: "5秒",
    icon: Sparkles,
  },
  {
    id: "i2v_high",
    mode: "i2v",
    name: "プロンプト生成（高品質）",
    description: "1080pでくっきり、5秒のAI動画。",
    tickets: 50,
    jpy: 500,
    estimatedTime: "約8〜12分で完成",
    resolution: "1080p",
    duration: "5秒",
    icon: Wand2,
  },
  {
    id: "i2v_long",
    mode: "i2v",
    name: "プロンプト生成（ロング）",
    description: "1080p・10秒のAI動画。じっくり動かしたい時に。",
    tickets: 90,
    jpy: 900,
    estimatedTime: "約15〜20分で完成",
    resolution: "1080p",
    duration: "10秒",
    icon: Wand2,
  },
  {
    id: "story_std",
    mode: "story",
    name: "ストーリーリール（標準）",
    description: "1枚の画像から最大5シーンの繋がった縦動画を1本生成。SNSリール向け。",
    tickets: 120,
    jpy: 1200,
    estimatedTime: "約15〜20分で完成",
    resolution: "720p",
    duration: "シーン合計（最大15秒）",
    hasAudio: true,
    icon: Sparkles,
  },
  {
    id: "story_pro",
    mode: "story",
    name: "ストーリーリール（高品質）",
    description: "1080pでくっきり、最大5シーンを1本のリールに。",
    tickets: 220,
    jpy: 2200,
    estimatedTime: "約20〜25分で完成",
    resolution: "1080p",
    duration: "シーン合計（最大15秒）",
    hasAudio: true,
    icon: Wand2,
  },
  {
    id: "lipsync",
    mode: "lipsync",
    name: "喋らせる（リップシンク）",
    description: "写真の人物に喋らせます。台本テキストに沿って自然な口の動きを生成。",
    tickets: 5,
    jpy: 50,
    estimatedTime: "約3〜5分で完成",
    resolution: "720p",
    duration: "口の動きに合わせて自動",
    needsScript: true,
    icon: Mic,
  },
];

type AspectRatio = "9:16" | "1:1" | "16:9";

const ASPECT_RATIO_TIERS: TierId[] = [
  "standard",
  "i2v_standard",
  "i2v_high",
  "i2v_long",
  "story_std",
  "story_pro",
];

const ASPECT_OPTIONS: { id: AspectRatio; label: string; sub: string }[] = [
  { id: "9:16", label: "縦長", sub: "9:16 / TikTok・Reels" },
  { id: "1:1", label: "正方形", sub: "1:1 / Instagram" },
  { id: "16:9", label: "横長", sub: "16:9 / YouTube" },
];

type QualityState = {
  tierId: TierId;
  removeWatermark: boolean;
  script?: string;
  videoPrompt?: string;
  aspectRatio?: AspectRatio;
};

function readQualityState(): QualityState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(QUALITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeQualityState(s: QualityState) {
  sessionStorage.setItem(QUALITY_KEY, JSON.stringify(s));
}

function readWizardMode(): "faceswap" | "i2v" | "story" {
  if (typeof window === "undefined") return "faceswap";
  try {
    const raw = sessionStorage.getItem(WIZARD_KEY);
    if (!raw) return "faceswap";
    const s = JSON.parse(raw);
    if (s.mode === "i2v") return "i2v";
    if (s.mode === "story") return "story";
    return "faceswap";
  } catch {
    return "faceswap";
  }
}

function hasWizardAssets(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = sessionStorage.getItem(WIZARD_KEY);
    if (!raw) return false;
    const s = JSON.parse(raw);
    if (!s.image) return false;
    if (s.mode === "i2v" || s.mode === "story") return true;
    return !!s.video;
  } catch {
    return false;
  }
}

function QualityPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [userId, setUserId] = useState<string | null>(null);
  const balance = useCredits(userId);

  const [selected, setSelected] = useState<TierId | null>(null);
  const removeWatermark = false;
  const [script, setScript] = useState("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("9:16");
  const [trialRemaining, setTrialRemaining] = useState<number | null>(null);
  const [wizardMode, setWizardMode] = useState<"faceswap" | "i2v" | "story">("faceswap");

  useEffect(() => {
    // ?image=... &video=... が付いていれば、既存wizard stateに素材を引き継ぎ
    if (search.image && search.video && typeof window !== "undefined") {
      try {
        const raw = sessionStorage.getItem(WIZARD_KEY);
        const prev = raw ? JSON.parse(raw) : {};
        const merged = {
          ...prev,
          image: prev.image ?? {
            path: "",
            signedUrl: search.image,
            name: "前回の画像",
            size: 0,
            // path: "" は再アップロード不可・引き継ぎ専用素材を示す
          },
          video: prev.video ?? {
            path: "",
            signedUrl: search.video,
            name: "前回の動画",
            size: 0,
            durationSec: 0,
          },
        };
        sessionStorage.setItem(WIZARD_KEY, JSON.stringify(merged));
      } catch (e) {
        console.warn("failed to seed wizard state from query", e);
      }
    }

    if (!hasWizardAssets()) {
      toast.error("先に素材をアップロードしてください");
      navigate({ to: "/create" });
      return;
    }
    setWizardMode(readWizardMode());

    let mounted = true;

    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data.user?.id ?? null;
      if (!mounted) return;
      setUserId(uid);
      if (!uid) {
        setTrialRemaining(null);
        return;
      }

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("trial_uses_count")
        .eq("user_id", uid)
        .maybeSingle();

      if (!mounted) return;
      if (error) {
        console.error("trial_uses_count load error:", error);
        setTrialRemaining(null);
        return;
      }

      const used = profile?.trial_uses_count ?? 0;
      setTrialRemaining(Math.max(0, 2 - used));
    });

    const prev = readQualityState();
    if (prev) {
      setSelected(prev.tierId);
      
      if (prev.script) setScript(prev.script);
      if (prev.videoPrompt) setVideoPrompt(prev.videoPrompt);
      if (prev.aspectRatio) setAspectRatio(prev.aspectRatio);
    }
    // wizard 側で入力された i2v プロンプト or テンプレ推奨プロンプトを初期値に
    try {
      const wiz = JSON.parse(sessionStorage.getItem(WIZARD_KEY) || "{}");
      const seed = wiz?.videoPrompt || wiz?.templatePrompt;
      if (seed) {
        if (wiz.mode === "i2v" && !prev?.videoPrompt) {
          setVideoPrompt(String(seed).slice(0, 500));
        } else if (wiz.mode !== "i2v" && !prev?.script) {
          setScript(String(seed).slice(0, 300));
        }
      }
    } catch {
      /* noop */
    }

    return () => {
      mounted = false;
    };
  }, [navigate, search.image, search.video]);

  const selectedTier = useMemo(
    () => TIERS.find((t) => t.id === selected) ?? null,
    [selected],
  );

  const totalCost = useMemo(() => {
    if (!selectedTier) return 0;
    return selectedTier.tickets + (removeWatermark ? WATERMARK_REMOVE_COST : 0);
  }, [selectedTier, removeWatermark]);

  const balanceLoaded = balance !== null;
  const canAfford = (cost: number) => balanceLoaded && balance! >= cost;
  const isTrialExhausted = (tierId: TierId) =>
    tierId === "trial" && trialRemaining !== null && trialRemaining <= 0;

  const handleSelect = (tier: Tier) => {
    if (!canAfford(tier.tickets)) return;
    if (isTrialExhausted(tier.id)) return;
    setSelected(tier.id);
  };

  const handleNext = () => {
    if (!selectedTier) return;
    if (isTrialExhausted(selectedTier.id)) {
      toast.error("お試しの利用回数（2回）を使い切りました");
      return;
    }
    if (selectedTier.needsScript && !script.trim()) {
      toast.error("喋らせる内容を入力してください");
      return;
    }
    if (!canAfford(totalCost)) {
      toast.error("クレジットが足りません");
      return;
    }
    if (selectedTier.mode === "i2v" && !videoPrompt.trim()) {
      toast.error("プロンプトを入力してください");
      return;
    }
    const supportsAspect = ASPECT_RATIO_TIERS.includes(selectedTier.id);
    const state: QualityState = {
      tierId: selectedTier.id,
      removeWatermark,
      script: selectedTier.needsScript ? script.trim() : undefined,
      videoPrompt: selectedTier.mode === "i2v" ? videoPrompt.trim() : undefined,
      aspectRatio: supportsAspect ? aspectRatio : undefined,
    };
    writeQualityState(state);
    navigate({ to: "/create/confirm" });
  };

  return (
    <main className="min-h-screen bg-background px-4 py-8 pb-32 sm:py-10">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10">
          <StepIndicator current={2} />
        </div>

        <header className="mb-6 text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">品質を選択</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            生成したい動画の品質を選んでください。
          </p>
        </header>

        <div className="mb-8 flex justify-center">
          <button
            type="button"
            onClick={() => navigate({ to: "/credits-shop" })}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium shadow-sm transition hover:border-primary hover:bg-primary/5"
          >
            <Gem className="h-4 w-4 text-primary" />
            残り
            <span className="text-base font-bold text-primary">
              {balance ?? "—"}
            </span>
            クレジット
          </button>
        </div>

        <div className="space-y-4">
          {TIERS.filter((t) => {
            // wizardMode に応じて表示するティアを絞り込み
            // lipsync は「声を変える」導線に置き換えたため非表示
            if (t.mode === "lipsync") return false;
            if (wizardMode === "i2v") return t.mode === "i2v";
            if (wizardMode === "story") return t.mode === "story";
            return t.mode === "faceswap";
          }).map((tier) => {
            const affordable = canAfford(tier.tickets);
            const trialOut = isTrialExhausted(tier.id);
            const disabled = !affordable || trialOut;
            const isSelected = selected === tier.id;
            const Icon = tier.icon;
            return (
              <Card
                key={tier.id}
                className={cn(
                  "relative overflow-hidden p-5 transition-all",
                  !disabled && "cursor-pointer hover:border-primary/60",
                  isSelected && "border-primary ring-2 ring-primary/40",
                  disabled && "opacity-60",
                )}
                onClick={() => !disabled && handleSelect(tier)}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  {/* Sample placeholder */}
                  <div className="relative flex h-32 w-full shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 via-primary/10 to-transparent sm:h-24 sm:w-32">
                    <Icon className="h-10 w-10 text-primary/70" />
                    {isSelected && (
                      <div className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold sm:text-xl">{tier.name}</h2>
                      {tier.hasAudio && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          音声あり
                        </span>
                      )}
                      {tier.id === "trial" && trialRemaining !== null && (
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            trialRemaining > 0
                              ? "bg-primary/10 text-primary"
                              : "bg-destructive/10 text-destructive",
                          )}
                        >
                          {trialRemaining > 0
                            ? `残り${trialRemaining}回`
                            : "上限到達"}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{tier.description}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>解像度: {tier.resolution}</span>
                      <span>長さ: {tier.duration}</span>
                      <span>{tier.estimatedTime}</span>
                    </div>
                  </div>

                  {/* Cost / action */}
                  <div className="flex flex-col items-stretch gap-2 sm:items-end">
                    <div className="flex items-baseline gap-1 sm:justify-end">
                      <Gem className="h-5 w-5 text-primary" />
                      <span className="text-2xl font-extrabold text-primary">
                        {tier.tickets}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        クレジット
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground sm:text-right">
                      （{tier.jpy.toLocaleString()}円相当）
                    </span>

                    {trialOut ? (
                      <span className="text-xs font-medium text-destructive sm:text-right">
                        お試しは2回まで
                      </span>
                    ) : affordable ? (
                      <Button
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(tier);
                        }}
                      >
                        {isSelected ? "選択中" : "これを選ぶ"}
                      </Button>
                    ) : (
                      <div className="flex flex-col items-stretch gap-1 sm:items-end">
                        <span className="text-xs font-medium text-destructive">
                          クレジットが足りません
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate({ to: "/credits-shop" });
                          }}
                        >
                          クレジット購入
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lipsync script input */}
                {isSelected && tier.needsScript && (
                  <div className="mt-4 border-t border-border pt-4">
                    <Label htmlFor="lipsync-script" className="text-sm font-semibold">
                      喋らせる内容
                    </Label>
                    <Textarea
                      id="lipsync-script"
                      value={script}
                      onChange={(e) => setScript(e.target.value)}
                      placeholder="例：こんにちは、今日はいい天気ですね。"
                      className="mt-2 min-h-[90px]"
                      maxLength={300}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <p className="mt-1 text-right text-xs text-muted-foreground">
                      {script.length} / 300
                    </p>
                  </div>
                )}

                {/* i2v prompt input */}
                {isSelected && tier.mode === "i2v" && (
                  <div className="mt-4 border-t border-border pt-4">
                    <Label htmlFor="i2v-prompt" className="text-sm font-semibold">
                      動かし方のプロンプト
                    </Label>
                    <Textarea
                      id="i2v-prompt"
                      value={videoPrompt}
                      onChange={(e) => setVideoPrompt(e.target.value)}
                      placeholder="例：人物がゆっくり振り向いて微笑む、髪が風になびく"
                      className="mt-2 min-h-[90px]"
                      maxLength={2500}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <p className="mt-1 text-right text-xs text-muted-foreground">
                      {videoPrompt.length} / 2500
                    </p>
                  </div>
                )}
              </Card>
            );
          })}

          {/* リップシンクの代わりに「声を変える」導線 */}
          <Card className="p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex h-32 w-full shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 via-primary/10 to-transparent sm:h-24 sm:w-32">
                <Mic className="h-10 w-10 text-primary/70" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold sm:text-xl">声を変える</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  完成した動画の声を、別の声に置き換えられます。動画ができたあとに音声変換ページで実行してください。
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:items-end">
                <VoiceChangeCta variant="outline" />
              </div>
            </div>
          </Card>
        </div>


        {/* Aspect ratio */}
        {selectedTier && (
          <Card className="mt-6 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">アスペクト比（縦・正方形・横）</span>
            </div>
            {ASPECT_RATIO_TIERS.includes(selectedTier.id) ? (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {ASPECT_OPTIONS.map((opt) => {
                  const active = aspectRatio === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setAspectRatio(opt.id)}
                      className={cn(
                        "flex flex-col items-center gap-2 rounded-lg border p-3 text-center transition",
                        active
                          ? "border-primary bg-primary/5 ring-2 ring-primary/40"
                          : "border-border hover:border-primary/50",
                      )}
                    >
                      <div
                        className={cn(
                          "rounded border-2 bg-muted",
                          active ? "border-primary" : "border-muted-foreground/40",
                          opt.id === "9:16" && "h-12 w-7",
                          opt.id === "1:1" && "h-10 w-10",
                          opt.id === "16:9" && "h-7 w-12",
                        )}
                      />
                      <div>
                        <div className="text-sm font-semibold">{opt.label}</div>
                        <div className="text-[10px] text-muted-foreground">{opt.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                {selectedTier.mode === "lipsync"
                  ? "リップシンクは元の写真の比率がそのまま使われます。"
                  : "ベースとなる動画の比率がそのまま使われます。"}
              </p>
            )}
          </Card>
        )}

      </div>

      {/* Sticky footer Next */}
      {selectedTier && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 px-4 py-4 shadow-lg backdrop-blur">
          <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-3 sm:flex-row">
            <div className="text-sm">
              <span className="text-muted-foreground">合計：</span>
              <span className="text-lg font-bold text-primary">
                <Gem className="mr-1 inline h-4 w-4" />
                {totalCost} クレジット
              </span>
              {balanceLoaded && (
                <span className="ml-2 text-xs text-muted-foreground">
                  （残高 {balance} → {Math.max(0, balance! - totalCost)}）
                </span>
              )}
            </div>
            <Button
              size="lg"
              onClick={handleNext}
              disabled={!canAfford(totalCost)}
              className="h-12 px-8 font-semibold bg-gradient-brand"
            >
              次へ：内容を確認
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
